import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './VehicleListTable.module.scss';
import { DataTableWithSidebar, DataTableColumn, OrderData } from 'common/components/dataTable';
import { VehicleViewModel } from 'api/vehicles/models/VehicleViewModel';
import { DefaultAssetImage } from 'Config';
import { UserProfile } from 'api/account/models/UserProfile';
import { Reducers } from 'store/types';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import Dropdown from 'common/components/dropdown/Dropdown';
import DropdownItem from 'common/components/dropdown/DropdownItem';
import { FaEllipsisH } from 'react-icons/fa';
import GaugeIcon from 'assets/svg/gauge.svg';
import IconWithText from 'common/components/iconWithText/IconWithText';
import AvatarWithText from 'common/components/avatar/AvatarWithText';
import DeletedVehicleCircle from 'common/components/deletedVehicleCircle/DeletedVehicleCircle';
import AlertsCounter from 'common/components/alertsCounter/AlertsCounter';
import MoneyFormat from 'common/components/moneyFormat/MoneyFormat';
import CurrentLocation from 'screens/vehicles-gps/components/currentLocation/CurrentLocation';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import { TrackingItemDto } from 'api/tracking/models/TrackingItemDto';
import { VehiclesSearchCriteria } from 'api/vehicles/models/VehiclesSearchCriteria';
import { useAdditionalDataListingColumns } from 'common/hooks/useAdditionalDataListingColumns';

// Extended type for table rows with all needed fields
type VehicleTableRow = VehicleViewModel & {
    vehicleImage: string;
    vehicleRegistration: string;
    vehicleAlerts: number;
    vehicleBrand: string;
    vehicleModel: string;
    vehicleSegment: string;
    vehicleFuelsDisplay: string;
    vehicleColorDisplay: string;
    vehicleLocalDisplay: string;
    vehicleDriverDisplay: string;
    vehicleKilometers: number;
    vehicleCostAverage: number;
    vehicleGpsDisplay: string;
    additionalDataValues?: Record<string, string>;
};

export type Props = {
    vehicles: VehicleViewModel[];
    hasCountersPolicy: boolean;
    hasGpsPolicy: boolean;
    hasAlertsReadPolicy: boolean;
    trackingVehicles: TrackingItemDto[] | null;
    privacyStatusMap?: Record<string, import('api/tracking/TrackingService').PrivacyStatus>;
    isLoadingMore: boolean;
    hasMore: boolean;
    onLoadMore: () => void;
    onClickItem: (vehicle: VehicleViewModel, event: any) => void;
    onOrderChange: (column: string, isAsc: boolean) => void;
    criteria: VehiclesSearchCriteria;
    scrollToRowId?: string | null;
    // Bidirectional scroll support
    hasMorePrevious?: boolean;
    isLoadingPrevious?: boolean;
    onLoadPrevious?: () => void;
    // Excel export
    onFetchAllForExport?: () => Promise<VehicleViewModel[]>;
    exportRef?: React.MutableRefObject<{ exportExcel: () => Promise<void> } | null>;
};

const VehicleListTable = ({
    vehicles,
    hasCountersPolicy,
    hasGpsPolicy,
    hasAlertsReadPolicy,
    trackingVehicles,
    privacyStatusMap,
    isLoadingMore,
    hasMore,
    onLoadMore,
    onClickItem,
    onOrderChange,
    criteria,
    scrollToRowId,
    hasMorePrevious,
    isLoadingPrevious,
    onLoadPrevious,
    onFetchAllForExport,
    exportRef,
}: Props) => {
    const { t } = useTranslation();

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasVehiclesWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_VEHICLES_WRITE']);
    const hasPrivacyStatusPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_PRIVACY_STATUS_READ']);

    const { columns: customFieldColumns } = useAdditionalDataListingColumns<VehicleTableRow>('VEHICLE');

    // Hydrate vehicle with GPS data
    const hydrateTracking = (v: VehicleViewModel): VehicleViewModel => {
        const trackingVehicle = trackingVehicles?.find(tv => tv.licensePlate === v.registrationNumber || tv.id === v.trackingDeviceId);
        if (trackingVehicle) {
            return {
                ...v,
                ignition: trackingVehicle.ignition,
                latitude: trackingVehicle.latitude,
                longitude: trackingVehicle.longitude,
                movement: trackingVehicle.movement,
                date: trackingVehicle.date,
                gpsDataHydrated: true,
            };
        } else if (trackingVehicles || !hasGpsPolicy) {
            return { ...v, gpsDataHydrated: true };
        }
        return v;
    };

    const renderGpsInfo = (vehicle: VehicleViewModel) => {
        const privacyStatus = vehicle.id ? privacyStatusMap?.[vehicle.id] : undefined;

        // Privacy badge supersedes address. Either policy is enough to see it:
        // PRIVACY_STATUS_READ gives the right to know; GPS_READ implies the right too,
        // since the user would otherwise see the location and needs to know why it's hidden.
        if ((hasPrivacyStatusPolicy || hasGpsPolicy) && privacyStatus?.isPrivate) {
            return <CurrentLocation privacyStatus={privacyStatus} />;
        }

        if (!hasGpsPolicy) {
            return <>-</>;
        }

        if (vehicle.trackingDeviceId && !vehicle.gpsDataHydrated) {
            return <LoadingSpinner />;
        }

        if (vehicle.trackingDeviceId && vehicle.gpsDataHydrated && !vehicle.latitude) {
            return <span className={styles.noGpsData}>{t('location.info_messages.no_data')}</span>;
        }

        if (vehicle.trackingDeviceId && vehicle.latitude && vehicle.longitude) {
            return (
                <CurrentLocation
                    position={{
                        ignition: vehicle.ignition,
                        latitude: vehicle.latitude,
                        longitude: vehicle.longitude,
                        date: vehicle.date!,
                        address: vehicle.address,
                        lastTripId: vehicle.lastTripId
                    }}
                />
            );
        }

        return <>-</>;
    };

    const renderTableActionCell = (row: VehicleTableRow) => {
        const urlDetails = `/vehicles/details/${row.id}`;
        const urlEdit = `/vehicles/edit/${row.id}`;

        return (
            <Dropdown
                options={
                    <>
                        <DropdownItem url={urlDetails}>
                            {t('common.details')}
                        </DropdownItem>
                        {hasVehiclesWritePolicy && (
                            <DropdownItem url={urlEdit}>
                                {t('common.edit')}
                            </DropdownItem>
                        )}
                    </>
                }
            >
                <div>
                    <FaEllipsisH />
                </div>
            </Dropdown>
        );
    };

    const tableColumns: DataTableColumn<VehicleTableRow>[] = useMemo(() => {
        const cols: DataTableColumn<VehicleTableRow>[] = [
            // Image
            {
                field: 'vehicleImage',
                name: t('common.image') as string,
                hideOrderButton: true,
                width: 75,
                hideOn: ['sm', 'md'],
                excludeFromExport: true,
                renderCell: row => (
                    <div className={styles.imageCell}>
                        <div
                            className={styles.img}
                            style={{
                                backgroundImage: `url(${row.photoUrl || row.vehicleSegmentDefaultPhotoUrl || DefaultAssetImage})`
                            }}
                        />
                    </div>
                ),
            },
            // Registration
            {
                field: 'vehicleRegistration',
                name: t('vehicle.registration') as string,
                orderColumn: 'v.registration_number',
                width: 130,
                renderCell: row => (
                    <div className={styles.registrationCell}>
                        <span className={styles.registrationNumber}>{row.registrationNumber}</span>
                        {!row.active && <DeletedVehicleCircle id={row.id ?? ''} />}
                    </div>
                ),
            },
        ];

        // Alerts column (conditional - only if user has policy)
        if (hasAlertsReadPolicy) {
            cols.push({
                field: 'vehicleAlerts',
                name: t('alerts.title') as string,
                orderColumn: 'valerts.total',
                width: 80,
                cellAlignment: 'center',
                columnCellAlignment: 'center',
                renderCell: row => (
                    <div className={styles.alertsCell}>
                        {row.totalAlerts && row.totalAlerts > 0
                            ? <AlertsCounter totalAlerts={row.totalAlerts} className={styles.alertsBadge} />
                            : <>-</>}
                    </div>
                ),
            });
        }

        cols.push(
            // Brand
            {
                field: 'vehicleBrand',
                name: t('vehicle.brand') as string,
                orderColumn: 'vb.name',
                width: 100,
                hideOn: ['sm'],
                renderCell: row => <>{row.vehicleBrandName || '-'}</>,
            },
            // Model
            {
                field: 'vehicleModel',
                name: t('vehicle.model') as string,
                orderColumn: 'vm.name',
                width: 100,
                hideOn: ['sm'],
                renderCell: row => <>{row.vehicleModelName || '-'}</>,
            },
            // Segment
            {
                field: 'vehicleSegment',
                name: t('vehicle.type') as string,
                orderColumn: 'vs.name',
                width: 100,
                hideOn: ['sm', 'md'],
                renderCell: row => <>{row.vehicleSegmentName || '-'}</>,
            },
            // Local
            {
                field: 'vehicleLocalDisplay',
                name: t('vehicle.local') as string,
                hideOrderButton: true,
                width: 130,
                hideOn: ['sm', 'md', 'lg'],
                renderCell: row => <>{row.vehicleLocals?.map(l => l.localName).join(', ') || '-'}</>,
            },
            // Fuel
            {
                field: 'vehicleFuelsDisplay',
                name: t('vehicle.fuel') as string,
                hideOrderButton: true,
                width: 100,
                hideOn: ['sm', 'md', 'lg'],
                exportValue: row => row.vehicleFuels?.map(f => t(('vehicle.fuels.' + f.fuelName) as any)).join(', ') || '',
                renderCell: row => {
                    const fuelsText = row.vehicleFuels?.map(f => t(('vehicle.fuels.' + f.fuelName) as any)).join(', ') || '-';
                    return <>{fuelsText}</>;
                },
            },
            // Color
            {
                field: 'vehicleColorDisplay',
                name: t('vehicle.color') as string,
                orderColumn: 'c.name',
                width: 80,
                hideOn: ['sm', 'md', 'lg'],
                exportValue: row => row.colorName ? t(('vehicle.colors.' + row.colorName) as any) as string : '',
                renderCell: row => {
                    const colorText = row.colorName ? t(('vehicle.colors.' + row.colorName) as any) : '-';
                    return <>{colorText}</>;
                },
            },
            // Driver
            {
                field: 'vehicleDriverDisplay',
                name: t('vehicle.default_driver') as string,
                orderColumn: 'driver.real_name',
                width: 150,
                hideOn: ['sm', 'md'],
                renderCell: row => row.driverName
                    ? <AvatarWithText src={row.driverPhotoUrl} size="x2">{row.driverName}</AvatarWithText>
                    : <>-</>,
            },
            // Kilometers
            {
                field: 'vehicleKilometers',
                name: t('vehicles.list.kilometers') as string,
                orderColumn: 'v.current_kilometers',
                cellAlignment: 'right',
                columnCellAlignment: 'right',
                width: 100,
                hideOn: ['sm'],
                headerNote: `${t('common.unit')}: ${t('vehicles.list.kilometers_minified')}`,
                renderCell: row => (
                    <IconWithText
                        icon={GaugeIcon}
                        value={row.currentKilometers}
                        suffix={t('vehicles.list.kilometers_minified') as string}
                        isNumeric={true}
                    />
                ),
            }
        );

        // Cost column (conditional)
        if (hasCountersPolicy) {
            cols.push({
                field: 'vehicleCostAverage',
                name: t('vehicles.list.average_monthly_cost') as string,
                orderColumn: 'monthly_cost_average',
                cellAlignment: 'right',
                columnCellAlignment: 'right',
                width: 120,
                hideOn: ['sm', 'md'],
                headerNote: `${t('common.unit')}: ${t('vehicles.list.euro_months')}`,
                renderCell: row => (
                    <MoneyFormat
                        className={styles.fontBold}
                        value={row.monthlyCostAverage}
                        suffix={t('vehicles.list.euro_months') as string}
                        fixedDecimalScale={false}
                    />
                ),
            });
        }

        // GPS column (conditional)
        if (hasGpsPolicy) {
            cols.push({
                field: 'vehicleGpsDisplay',
                name: t('location.title') as string,
                hideOrderButton: true,
                width: 150,
                hideOn: ['sm', 'md', 'lg'],
                excludeFromExport: true,
                renderCell: row => renderGpsInfo(row),
            });
        }

        cols.push(...customFieldColumns);

        return cols;
    }, [hasCountersPolicy, hasGpsPolicy, hasAlertsReadPolicy, hasPrivacyStatusPolicy, userProfile, t, trackingVehicles, privacyStatusMap, customFieldColumns]);

    const toTableRow = (v: VehicleViewModel): VehicleTableRow => ({
        ...v,
        vehicleImage: v.photoUrl || v.vehicleSegmentDefaultPhotoUrl || '',
        vehicleRegistration: v.registrationNumber || '',
        vehicleAlerts: v.totalAlerts || 0,
        vehicleBrand: v.vehicleBrandName || '',
        vehicleModel: v.vehicleModelName || '',
        vehicleSegment: v.vehicleSegmentName || '',
        vehicleFuelsDisplay: v.vehicleFuels?.map(f => f.fuelName).join(', ') || '',
        vehicleColorDisplay: v.colorName || '',
        vehicleLocalDisplay: v.vehicleLocals?.map(l => l.localName).join(', ') || '',
        vehicleDriverDisplay: v.driverName || '',
        vehicleKilometers: v.currentKilometers || 0,
        vehicleCostAverage: v.monthlyCostAverage || 0,
        vehicleGpsDisplay: '',
    });

    // Convert rows to extended type for table
    const tableRows: VehicleTableRow[] = useMemo(() => {
        return vehicles.map(v => {
            const hydratedVehicle = hydrateTracking(v);
            return toTableRow(hydratedVehicle);
        });
    }, [vehicles, trackingVehicles]);

    // Fetch all vehicles for Excel export (converts to VehicleTableRow)
    const fetchAllForExcelExport = useCallback(async (): Promise<VehicleTableRow[]> => {
        if (!onFetchAllForExport) return [];
        const allVehicles = await onFetchAllForExport();
        return allVehicles.map(toTableRow);
    }, [onFetchAllForExport]);

    // Handle order change from DataTable
    const handleOrderChange = (orderData: OrderData<VehicleTableRow>) => {
        const orderColumn = orderData.orderColumn || (orderData.field as string);
        onOrderChange(orderColumn, orderData.isOrderAsc);
    };

    // Map orderColumn to field for proper sorting indicator
    const getFieldFromOrderColumn = (orderColumn: string | undefined): keyof VehicleTableRow => {
        const mapping: Record<string, keyof VehicleTableRow> = {
            'v.registration_number': 'vehicleRegistration',
            'valerts.total': 'vehicleAlerts',
            'vb.name': 'vehicleBrand',
            'vm.name': 'vehicleModel',
            'vs.name': 'vehicleSegment',
            'c.name': 'vehicleColorDisplay',
            'driver.real_name': 'vehicleDriverDisplay',
            'v.current_kilometers': 'vehicleKilometers',
            'monthly_cost_average': 'vehicleCostAverage',
        };
        return mapping[orderColumn ?? ''] ?? 'vehicleRegistration';
    };

    // Get default order from criteria
    const defaultOrder: OrderData<VehicleTableRow> = useMemo(() => {
        const orderColumn = criteria?.orderColumn ?? 'v.registration_number';
        return {
            field: getFieldFromOrderColumn(orderColumn),
            orderColumn: orderColumn,
            isOrderAsc: criteria?.orderBy === 'asc',
        };
    }, [criteria?.orderColumn, criteria?.orderBy]);

    return (
        <div className={styles.tableContainer}>
            <DataTableWithSidebar
                cacheKey="vehicles-list-v1"
                columns={tableColumns}
                rows={tableRows}
                defaultOrder={defaultOrder}
                defaultHiddenColumns={['vehicleSegment', 'vehicleLocalDisplay']}
                onRowClick={(row, _, event) => onClickItem(row, event)}
                onOrderChange={handleOrderChange}
                onRenderAction={renderTableActionCell}
                onHref={row => `/vehicles/details/${row.id}`}
                onRowId={row => row.id ?? ''}
                scrollToRowId={scrollToRowId}
                infiniteScroll={{
                    isLoading: isLoadingMore,
                    hasMore: hasMore,
                    onLoadMore: onLoadMore,
                    hasMorePrevious: hasMorePrevious,
                    isLoadingPrevious: isLoadingPrevious,
                    onLoadPrevious: onLoadPrevious,
                }}
                exportExcel={onFetchAllForExport ? {
                    fetchAll: fetchAllForExcelExport,
                    fileName: t('vehicles.title') as string,
                } : undefined}
                exportRef={exportRef}
                hideExportInSidebar={!!exportRef}
            />
        </div>
    );
};

export default VehicleListTable;
